import * as React from "react";
import classNames from "classnames";
import { Button, ButtonProps, makeStyles, Typography } from "@material-ui/core";

// styles
import { colors, greys } from "@styles/color.styles";
import spacing from "@styles/spacing.styles";

export interface CalloutProps {
  children?: React.HTMLAttributes<HTMLDivElement>["children"];
  style?: React.HTMLAttributes<HTMLDivElement>["style"];
  className?: string;
  variant?: "box";
  level?: "info" | "warning" | "error";
}

export interface CalloutButtonProps extends CalloutProps {
  buttonProps?: ButtonProps;
}

const useCalloutStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    padding: spacing.sm,
    "&.box": {
      borderTop: `1px solid ${greys.paleGrey}`,
      borderRight: `1px solid ${greys.paleGrey}`,
      borderBottom: `1px solid ${greys.paleGrey}`,
      borderLeft: `3px solid ${colors.blueCool.standard}`,
      "&.warning": {
        borderLeft: `3px solid ${colors.yellow.darker}`,
      },
      "&.error": {
        borderLeft: `3px solid ${colors.rose.standard}`,
      },
    },
  },
});

const useCalloutButtonStyles = makeStyles({
  button: {
    color: colors.blueCool.standard,
    borderColor: colors.blueCool.standard,
    "&.warning": {
      color: colors.yellow.darker,
      borderColor: colors.yellow.darker,
    },
    "&.error": {
      color: colors.rose.standard,
      borderColor: colors.rose.standard,
    },
  },
  typography: {
    flex: 1,
  },
});

export const Callout = ({
  children,
  style,
  className,
  variant = "box",
  level = "info",
}: CalloutProps) => {
  const classes = useCalloutStyles();

  return (
    <div style={style} className={classNames(classes.container, variant, level, className)}>
      {(typeof children === "string" && <Typography variant="body2">{children}</Typography>) ||
        children}
    </div>
  );
};

export const CalloutButton = ({
  children,
  buttonProps,
  level,
  ...calloutProps
}: CalloutButtonProps) => {
  const classes = useCalloutButtonStyles();

  return (
    <Callout {...calloutProps} level={level}>
      {(typeof children === "string" && (
        <Typography variant="body2" className={classes.typography}>
          {children}
        </Typography>
      )) ||
        children}
      <Button
        {...buttonProps}
        variant="outlined"
        className={classNames(classes.button, level, buttonProps?.className)}
      />
    </Callout>
  );
};
